import { useMemo, useState, useEffect, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import "./DataTable.css";
import { useTranslation } from "react-i18next";
import { ActionMenu } from "./ActionMenu";
import { appConfig } from "../../utils/config";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { extractIdFromIri } from "../../utils/extractFromIri";

const compareSelectionKeys = (left, right) => left.localeCompare(right);

const DataTable = ({
  data = [],
  columns,
  onEdit,
  onDelete,
  onDetails,
  onOpen,
  onActivity,
  onCloseSession,
  onDeleteUser,
  onInhability,
  onPermission,
  onNegotiate,
  onRevoke,
  getRevokeLabel,
  getAdditionalActions,
  shouldHideActions,
  showActions = true,
  initialSorting = [],
  onRowClick,
  pagination,
  setPagination,
  rowCount,
  enableRowSelection = false,
  selectionMode = "single",
  getRowId = (row, index) => row?.id ?? row?.identifier ?? String(index),
  selectedRowIds,
  onSelectedRowsChange,
}) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState(initialSorting);
  const { isProvider, decoded, isAdmin } = useAuthRoles();
  const [rowSelectionInternal, setRowSelectionInternal] = useState({});
  const rowSelection = useMemo(
    () =>
      enableRowSelection ? (selectedRowIds ?? rowSelectionInternal) : {},
    [enableRowSelection, selectedRowIds, rowSelectionInternal],
  );
  const tablePagination = useMemo(
    () => ({
      pageIndex: Math.max((pagination?.pageIndex ?? 1) - 1, 0),
      pageSize: pagination?.pageSize ?? 10,
    }),
    [pagination],
  );

  const selectionColumn = useMemo(() => {
    if (!enableRowSelection) return null;

    return {
      id: "__select",
      header: () => null,
      cell: ({ row, table }) => (
        <input
          type="checkbox"
          className="dt-checkbox"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={(e) => {
            if (selectionMode === "single") {
              table.setRowSelection({ [row.id]: e.target.checked });
              return;
            }
            row.getToggleSelectedHandler()(e);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("datatable.selectRow")}
        />
      ),
      meta: { width: "48px" },
      enableSorting: false,
    };
  }, [enableRowSelection, selectionMode, t]);

  const tableColumns = useMemo(() => {
    const baseColumns = showActions
      ? [
          ...columns,
          {
            header: t("datatable.actions"),
            id: "actions",
            cell: ({ row, table }) => {
              const visibleRowIndex = table
                .getRowModel()
                .rows.findIndex((visibleRow) => visibleRow.id === row.id);

              if (shouldHideActions?.(row.original, visibleRowIndex, row)) {
                return null;
              }

              const participantId = String(
                decoded?.participant_id || "",
              ).trim();
              const additionalActions = Array.isArray(
                getAdditionalActions?.(row.original),
              )
                ? getAdditionalActions(row.original)
                : [];

              const canEditOrDelete =
                isAdmin ||
                (isProvider &&
                  [
                    row.original.publisher,
                    row.original.assigner,
                    ...(Array.isArray(row.original.items)
                      ? row.original.items.map((item) => item?.metadata?.publisher)
                      : []),
                  ].some((value) => {
                    const id = extractIdFromIri(value) || value;
                    return String(id || "").trim() === participantId;
                  }));

              const actions = [
                canEditOrDelete &&
                  onEdit && {
                    label: t("datatable.edit"),
                    onClick: () => onEdit(row.original),
                  },
                canEditOrDelete &&
                  onDelete && {
                    label: t("datatable.delete"),
                    onClick: () => onDelete(row.original),
                  },
                onDetails && {
                  label: t("datatable.details"),
                  onClick: () => onDetails(row.original),
                },
                onOpen && {
                  label: t("datatable.open"),
                  onClick: () => onOpen(row.original),
                },
                onCloseSession &&
                  row.original.hasActiveSession && {
                    label: t("datatable.close"),
                    onClick: () => onCloseSession(row.original),
                  },
                onInhability && {
                  label: row.original.enabled
                    ? t("datatable.inhability")
                    : t("datatable.enable"),
                  onClick: () => onInhability(row.original),
                },
                onPermission && {
                  label: t("datatable.permission"),
                  onClick: () => onPermission(row.original),
                },
                onDeleteUser && {
                  label: t("datatable.deleteUser"),
                  onClick: () => onDeleteUser(row.original),
                },
                onNegotiate && {
                  label: t("datatable.negotiate"),
                  onClick: () => onNegotiate(row.original),
                },
                onRevoke &&
                  (() => {
                    const label =
                      getRevokeLabel?.(row.original) ??
                      t("participant.approval.action");
                    if (!label) return null;

                    return {
                      label,
                      onClick: () => onRevoke(row.original),
                    };
                  })(),
                ...additionalActions,
                onActivity && {
                  label: t("datatable.activity"),
                  onClick: () => onActivity(row.original),
                },
              ].filter(Boolean);

              if (actions.length === 0) return null;

              return <ActionMenu actions={actions} />;
            },
          },
        ]
      : columns;

    if (!selectionColumn) return baseColumns;
    return [selectionColumn, ...baseColumns];
  }, [
    columns,
    showActions,
    selectionColumn,
    t,
    onEdit,
    onDelete,
    onDetails,
    onOpen,
    onNegotiate,
    onRevoke,
    getRevokeLabel,
    getAdditionalActions,
    shouldHideActions,
    onInhability,
    onPermission,
    onDeleteUser,
    onActivity,
    onCloseSession,
    isProvider,
    decoded.participant_id,
    isAdmin,
  ]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    rowCount,
    state: {
      pagination: tablePagination,
      sorting,
      ...(enableRowSelection ? { rowSelection } : {}),
    },
    getRowId,
    enableRowSelection,
    onRowSelectionChange: enableRowSelection
      ? (updater) => {
          const next =
            typeof updater === "function" ? updater(rowSelection) : updater;

          if (selectedRowIds !== undefined) {
            if (onSelectedRowsChange) {
              const selectedInCurrentPage = data.filter((row, index) => {
                const id = String(getRowId(row, index));
                return Boolean(next?.[id]);
              });
              const nextSignature = Object.keys(next || {})
                .filter((k) => next[k])
                .sort(compareSelectionKeys)
                .join("|");
              lastEmittedSignatureRef.current = nextSignature;
              onSelectedRowsChange(selectedInCurrentPage, next || {});
            }
            return;
          }
          setRowSelectionInternal(next);
        }
      : undefined,

    manualPagination: true,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const current = {
          pageIndex: Math.max((prev?.pageIndex ?? 1) - 1, 0),
          pageSize: prev?.pageSize ?? 10,
        };
        const next = typeof updater === "function" ? updater(current) : updater;

        return {
          pageIndex: (next?.pageIndex ?? 0) + 1,
          pageSize: next?.pageSize ?? current.pageSize,
        };
      });
    },
  });

  const selectedRows = useMemo(() => {
    if (!enableRowSelection) return [];
    return table.getSelectedRowModel().rows.map((r) => r.original);
  }, [enableRowSelection, table]);

  const selectionSignature = useMemo(() => {
    if (!enableRowSelection) return "";
    return Object.keys(rowSelection)
      .filter((k) => rowSelection[k])
      .sort(compareSelectionKeys)
      .join("|");
  }, [enableRowSelection, rowSelection]);

  const lastEmittedSignatureRef = useRef("");

  useEffect(() => {
    if (!enableRowSelection || !onSelectedRowsChange) return;

    if (lastEmittedSignatureRef.current === selectionSignature) return;
    lastEmittedSignatureRef.current = selectionSignature;

    onSelectedRowsChange(selectedRows, rowSelection);
  }, [
    enableRowSelection,
    onSelectedRowsChange,
    selectionSignature,
    selectedRows,
    rowSelection,
  ]);

  const renderPageNumbers = () => {
    const pageCount = table.getPageCount();
    const currentPage = pagination.pageIndex;
    const pages = [];

    pages.push(1);
    if (currentPage > 4) pages.push("...");

    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(pageCount - 1, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }

    if (currentPage < pageCount - 3) pages.push("...");
    if (pageCount > 1) pages.push(pageCount);

    return pages.map((page, idx) =>
      page === "..." ? (
        <span key={"dots-" + idx} className="dots">
          ...
        </span>
      ) : (
        <button
          key={page}
          className={`page-btn ${page === currentPage ? "active" : ""}`}
          onClick={() => table.setPageIndex(page - 1)}
        >
          {page}
        </button>
      ),
    );
  };

  return (
    <div className="table-wrapper">
      <table className="generic-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const canSort = header.column.getCanSort();
                return (
                  <th
                    key={header.id}
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={{ width: header.column.columnDef.meta?.width }}
                    className={`${canSort ? "sortable-header" : ""} ${
                      appConfig.client === "CLIENT_B" ? "red" : "blue"
                    }`}
                  >
                    <div className="th-content">
                      <span className="th-label">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </span>

                      {canSort && (
                        <span
                          className={`sort-icon ${
                            header.column.getIsSorted() === "asc"
                              ? "asc"
                              : header.column.getIsSorted() === "desc"
                                ? "desc"
                                : "none"
                          } ${appConfig.client === "CLIENT_B" ? "red" : "blue"}`}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => {
              const cells = row.getVisibleCells();
              const lastCellIndex = cells.length - 1;

              return (
                <tr key={row.id}>
                  {cells.map((cell, index) => {
                    const isActionCell = showActions && index === lastCellIndex;
                    const isSelectionCell =
                      enableRowSelection && cell.column.id === "__select";

                    return (
                      <td key={cell.id}>
                        <div
                          className="cell-content"
                          style={{
                            cursor:
                              !isActionCell && !isSelectionCell && onRowClick
                                ? "pointer"
                                : "default",
                          }}
                          onClick={(e) => {
                            if (
                              !isActionCell &&
                              !isSelectionCell &&
                              onRowClick
                            ) {
                              if (e.target.closest('input[type="checkbox"]'))
                                return;
                              onRowClick(row.original);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (
                              (e.key === "Enter" || e.key === " ") &&
                              !isActionCell &&
                              !isSelectionCell &&
                              onRowClick
                            ) {
                              e.preventDefault();
                              onRowClick(row.original);
                            }
                          }}
                          role={
                            !isActionCell && !isSelectionCell && onRowClick
                              ? "button"
                              : undefined
                          }
                          tabIndex={
                            !isActionCell && !isSelectionCell && onRowClick
                              ? 0
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={table.getAllLeafColumns().length}>
                <div className="no-data">{t("datatable.noData")}</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {rowCount > pagination.pageSize && (
        <div className="table-pagination">
          <div className="table-info">
            {`${(pagination.pageIndex - 1) * pagination.pageSize + 1}-${Math.min(
              pagination.pageIndex * pagination.pageSize,
              rowCount,
            )} ${t("datatable.of")} ${rowCount} ${t("datatable.elements")}`}
          </div>

          <div className="table-pagination-buttons">
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              title={t("datatable.firstPage")}
            >
              {"<<"}
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              title={t("datatable.prevPage")}
            >
              {"<"}
            </button>

            <span>{renderPageNumbers()}</span>

            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              title={t("datatable.nextPage")}
            >
              {">"}
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              title={t("datatable.lastPage")}
            >
              {">>"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
